/**
 * @file Badge de statut client (icône seule, ou icône + libellé).
 * @module components.customer.CustomerStatusBadge
 *
 * Rendu UNIQUE et centralisé du badge de statut, utilisé partout où un statut
 * client s'affiche (listes clients, cellules de réservation, fiche…). Selon la
 * config du statut (`showLabel`), affiche l'icône seule (libellé en tooltip) ou
 * l'icône suivie du libellé. Ne rend rien si le statut n'a pas d'icône (ex.
 * client sans statut).
 */

import PropTypes from 'prop-types';
import {Box, Typography, Tooltip} from '@mui/material';
import {alpha} from '@mui/material/styles';

import Iconify from 'packages/common/components/iconify';

/**
 * Badge d'un statut client.
 *
 * @param {Object} props
 * @param {Object} [props.config] - Config visuelle du statut ({icon, color, label, showLabel, tooltip})
 * @param {number} [props.width=16] - Largeur de l'icône
 * @param {Object} [props.sx] - Styles additionnels sur le conteneur
 * @return {JSX.Element|null}
 */
export default function CustomerStatusBadge({config, width = 16, sx}) {
  if (!config || !config.icon) return null;
  const color = config.color || 'currentColor';

  if (config.showLabel) {
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          borderRadius: 0.75,
          bgcolor: alpha(color, 0.16),
          color,
          lineHeight: 1,
          ...sx,
        }}
      >
        <Iconify icon={config.icon} width={width} />
        <Typography component="span" variant="caption" sx={{fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap'}}>
          {config.label}
        </Typography>
      </Box>
    );
  }

  return (
    <Tooltip title={config.tooltip || config.label} arrow placement="top">
      <Box component="span" sx={{display: 'inline-flex', ...sx}}>
        <Iconify icon={config.icon} width={width} sx={{color}} />
      </Box>
    </Tooltip>
  );
}

CustomerStatusBadge.propTypes = {
  config: PropTypes.object,
  width: PropTypes.number,
  sx: PropTypes.object,
};
